import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  pmGet,
  pmPost,
  type PmBudgetTracker,
  type PmPayment,
  type PmProject,
  type PmTrackerOpportunity,
  type PmTrackerPhaseRow,
} from '../api';
import './BudgetTracker.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Budget Tracker — actual spend against the plan, per opportunity and phase.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/budget-tracker.ejs`.
 * The screen's own description of the one rule that matters here:
 *
 *   Every phase has an editable Actual box. A number you type is a manual actual and always
 *   wins. Leave it blank and the phase falls back to the sum of its logged payments (shown
 *   as the faint placeholder).
 *
 * SO THE ACTUAL HAS TWO SOURCES AND A FIXED PRECEDENCE, and the screen shows both at once:
 * the typed figure in the input, the ledger total as its placeholder. Clearing the input is
 * how you hand the phase back to the ledger — which is why an empty string maps to null on
 * save and never to 0.
 *
 * TWO TABS, ONE DATASET. The breakdown and the ledger read the same `payments` array, so a
 * payment logged on one tab moves the other tab's Actual immediately without a refetch.
 */

const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};

const fmt = (n: unknown) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

/**
 * The variance colour bands, and the half-dollar dead zone is the source's: a rounding
 * artefact of a fraction of a dollar reads as "on plan", not as an overspend.
 */
const varClass = (v: number) =>
  v < -0.5 ? 'pm-bt-varpos' : v > 0.5 ? 'pm-bt-varneg' : 'pm-bt-varzero';
/** A MINUS SIGN (−, U+2212), not a hyphen — the source's own character. */
const varStr = (v: number) => `${v > 0.5 ? '+' : v < -0.5 ? '−' : ''}${fmt(Math.abs(v))}`;

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/budget-tracker.ejs`,
 * its `btBreakdownHelp` / `btLedgerHelp` objects. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const BT_HELPS: HelpContent[] = [
  {
    tabLabel: 'Budget breakdown',
    title: 'Budget Tracker — Breakdown',
    icon: '💳',
    purpose: "Track <strong>actual spend against the plan</strong> for every opportunity in the project, phase by phase. A project-wide <strong>Shared / Project</strong> bucket sits alongside each <strong>opportunity</strong>, and each expands into its five delivery phases.",
    doHere: [
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong> &mdash; it appears here as a single <strong>Delivery</strong> row covering its whole window, and is fully trackable just like any other initiative.",
      "Pick a <strong>Project</strong> — its Shared bucket and opportunities load with the planned budget from the Budget Plan.",
      "Expand any scope and type an <strong>Actual</strong> straight into a phase row — every phase is editable.",
      "Or log individual payments on the <strong>Payments ledger</strong> tab; they roll up into the phase Actual automatically.",
      "Read the <strong>Variance</strong> column, then click <strong>Save</strong> to persist the actuals you typed."
    ],
    fits: 'Budget Plan &rarr; <strong>Budget Tracker</strong> &rarr; Execution Control Center',
    firstTime: "Pick a project, expand an opportunity, type an actual (or log payments), then Save.",
    sections: [
      { label: 'Planned, Actual & Variance', type: 'defs', items: [
        { term: 'Planned', color: '#607d8b', def: "The effective budget from the <strong>Budget Plan</strong> (override if set, else the resource-model proposal)." },
        { term: 'Actual', color: '#00897b', def: "What was actually spent — type it directly, or let logged payments fill it in." },
        { term: 'Variance', color: '#455a64', def: "Actual − Planned. <span style=\"color:#2e7d32;font-weight:700;\">Green</span> under plan, <span style=\"color:#c62828;font-weight:700;\">red</span> over." }
      ] },
      { label: 'Typing actuals vs. the ledger', type: 'html', html: "Every phase has an editable Actual box. A number you type is a <strong>manual actual</strong> and always wins. Leave it blank and the phase falls back to the sum of its <strong>logged payments</strong> (shown as the faint placeholder). Run the tracker purely by typing, purely by the ledger, or a mix." },
      { label: "What's new", type: 'html', html: "This screen is now <strong>per-opportunity</strong>: a Shared/Project bucket plus each opportunity, each broken down by phase (previously a single project-level view). Actuals are editable on <strong>every</strong> phase, and payments moved to their own <strong>Payments ledger</strong> tab." }
    ],
    links: [ { label: '← Budget Plan', href: '/project_management/program-management/budget-resources/budget-plan' } ]
  },
  {
    tabLabel: 'Payments ledger',
    title: 'Payments Ledger',
    icon: '🧾',
    purpose: "Record individual <strong>payments</strong> against a scope and phase and keep an auditable history. Each payment <strong>rolls up</strong> into that phase's Actual on the Budget breakdown tab.",
    doHere: [
      "Use the single <strong>add row</strong> at the top: choose Scope and Phase, then enter Vendor, Invoice #, Date and Amount.",
      "Click <strong>+ Add</strong> — the payment saves immediately and appears in the history below.",
      "The phase's Actual on the Breakdown tab updates automatically (unless a manual actual is typed there, which overrides it).",
      "Delete a payment with the <strong>×</strong> to remove it from the rollup."
    ],
    fits: 'Budget Tracker &rarr; <strong>Payments ledger</strong> (per opportunity &amp; phase)',
    firstTime: "Fill the top add-row (scope, phase, vendor, amount), click + Add, then check the Breakdown tab.",
    sections: [
      { label: 'What each column means', type: 'defs', items: [
        { term: 'Scope', color: '#6a1b9a', def: "Shared / Project, or a specific opportunity." },
        { term: 'Phase', color: '#1976d2', def: "Which of the five delivery phases the payment belongs to." },
        { term: 'Vendor', color: '#00897b', def: "Who was paid (free text). Invoice #, Date and Amount complete the record." }
      ] },
      { label: 'How it rolls up', type: 'html', html: "All payments for a given opportunity + phase are summed into that phase's <strong>Actual</strong> on the Breakdown tab. A <strong>manual actual</strong> typed there takes precedence; clear it to fall back to the payment total." },
      { label: "What's new", type: 'html', html: "Payments used to be a pop-up dialog; they now have this dedicated <strong>ledger tab</strong> with a single inline add-row and a full history." }
    ],
    links: [ { label: '← Budget Plan', href: '/project_management/program-management/budget-resources/budget-plan' } ]
  },
];

export function BudgetTracker() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [currentProject, setCurrentProject] = useState('');
  const [prog, setProg] = useState<PmBudgetTracker | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'breakdown' | 'ledger'>('breakdown');
  const [dirty, setDirty] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The inline add-row on the ledger tab.
  const [payScope, setPayScope] = useState('');
  const [payPhase, setPayPhase] = useState('');
  const [payVendor, setPayVendor] = useState('');
  const [payInvoice, setPayInvoice] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payAmount, setPayAmount] = useState('');

  useEffect(() => {
    pmGet<{ projects: PmProject[] }>('/br/projects')
      .then((d) => setProjects(d.projects ?? []))
      .catch((e) => setError((e as Error).message));
  }, []);

  const onSelect = useCallback(async (id: string) => {
    setCurrentProject(id);
    setError(null);
    if (!id) {
      setProg(null);
      return;
    }
    try {
      const d = await pmGet<PmBudgetTracker>(
        `/budget-tracker/program-data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setExpanded(new Set());
      setProg(d);
      setPayScope('');
      setPayPhase(d.phases[0]?.key ?? '');
      setDirty(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  const scopePhases = useCallback(
    (id: string): PmTrackerPhaseRow[] => {
      if (!prog) return [];
      if (id === 'shared') return prog.shared.phases;
      return prog.opportunities.find((x) => `opp:${x.opportunity_id}` === id)?.phases ?? [];
    },
    [prog],
  );

  /** `shared` carries no opportunity; every other scope id is `opp:<id>`. */
  const scopeOppId = (id: string) => (id === 'shared' ? '' : id.slice(4));

  const paymentsFor = useCallback(
    (oppId: string, phase: string) =>
      (prog?.payments ?? []).filter((p) => (p.opportunity_id || '') === oppId && p.phase === phase),
    [prog],
  );

  const actualOf = useCallback(
    (r: PmTrackerPhaseRow, oppId: string) => {
      if (r.manual != null && (r.manual as unknown) !== '') return Number(r.manual);
      return paymentsFor(oppId, r.phase).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    [paymentsFor],
  );

  const plannedTot = useCallback(
    (id: string) => scopePhases(id).reduce((s, r) => s + (Number(r.planned) || 0), 0),
    [scopePhases],
  );
  const actualTot = useCallback(
    (id: string) => {
      const oppId = scopeOppId(id);
      return scopePhases(id).reduce((s, r) => s + actualOf(r, oppId), 0);
    },
    [scopePhases, actualOf],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = (on: boolean) => {
    if (!prog) return;
    setExpanded(
      on
        ? new Set(['shared', ...prog.opportunities.map((o) => `opp:${o.opportunity_id}`)])
        : new Set(),
    );
  };

  const setManual = (scopeId: string, phase: string, val: string) => {
    const row = scopePhases(scopeId).find((x) => x.phase === phase);
    if (!row) return;
    row.manual = val === '' ? null : (val as unknown as number);
    setProg((p) => (p ? { ...p } : p));
    setDirty(true);
  };

  const save = async () => {
    if (!currentProject || !prog) return;
    setSaving(true);
    setSaveLabel('Saving…');
    const actual = (r: PmTrackerPhaseRow) =>
      (r.manual as unknown) === '' || r.manual == null ? null : Number(r.manual);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/budget-tracker/program-save', {
        delivery_project_id: currentProject,
        shared: prog.shared.phases.map((r) => ({ phase: r.phase, actual: actual(r) })),
        opportunities: prog.opportunities.map((o) => ({
          opportunity_id: o.opportunity_id,
          actuals: o.phases.map((r) => ({ phase: r.phase, actual: actual(r) })),
        })),
      });
      if (d.success) {
        setDirty(false);
        setSaveLabel('Saved ✓');
        setTimeout(() => setSaveLabel('Save'), 1100);
      } else {
        setError(d.error ?? 'failed');
        setSaveLabel('Save');
      }
    } catch (e) {
      setError((e as Error).message);
      setSaveLabel('Save');
    } finally {
      setSaving(false);
    }
  };

  const addPayment = async () => {
    if (!prog) return;
    if (payAmount === '' || isNaN(Number(payAmount))) {
      setError('Enter a valid amount.');
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; error?: string; id: number }>(
        '/budget-tracker/payment/program-save',
        {
          delivery_project_id: currentProject,
          payment: {
            opportunity_id: payScope || null,
            phase: payPhase,
            vendor: payVendor.trim(),
            amount: Number(payAmount),
            invoice_number: payInvoice.trim(),
            payment_date: payDate || null,
          },
        },
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setProg((p) =>
        p
          ? {
              ...p,
              payments: [
                ...p.payments,
                {
                  id: d.id,
                  opportunity_id: payScope,
                  phase: payPhase,
                  vendor: payVendor.trim(),
                  amount: Number(payAmount),
                  invoice_number: payInvoice.trim(),
                  payment_date: payDate,
                } as PmPayment,
              ],
            }
          : p,
      );
      setPayVendor('');
      setPayInvoice('');
      setPayDate('');
      setPayAmount('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const delPayment = async (pid: number) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/budget-tracker/payment/program-delete',
        { delivery_project_id: currentProject, id: pid },
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setProg((p) => (p ? { ...p, payments: p.payments.filter((x) => x.id !== pid) } : p));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const phaseLabel = (key: string) => prog?.phases.find((x) => x.key === key)?.label ?? key;
  const scopeName = (oppId: string) => (!oppId ? '🌐 Shared' : `📌 ${oppId}`);

  const totals = prog
    ? prog.opportunities.reduce(
        (acc, o) => {
          const k = `opp:${o.opportunity_id}`;
          return { planned: acc.planned + plannedTot(k), actual: acc.actual + actualTot(k) };
        },
        { planned: plannedTot('shared'), actual: actualTot('shared') },
      )
    : { planned: 0, actual: 0 };
  const totalVar = totals.actual - totals.planned;

  const renderScope = (id: string, title: string, op: PmTrackerOpportunity | null) => {
    const isShared = !op;
    const open = expanded.has(id);
    const accent = isShared ? '#6a1b9a' : '#00897b';
    const oppId = scopeOppId(id);
    const phases = scopePhases(id);
    const pl = plannedTot(id);
    const ac = actualTot(id);
    const tv = ac - pl;

    const rows = [
      <tr className="grp" key={id} onClick={() => toggle(id)}>
        <td className="ind">
          <i
            className="material-icons pm-bt-caret"
            style={{ transform: `rotate(${open ? 90 : 0}deg)` }}
          >
            chevron_right
          </i>
        </td>
        <td>
          <div className="pm-bt-scopecell">
            <span className="pm-bt-dot" style={{ background: accent }} />
            {title} {op ? <span className="pm-bt-oppname">{op.name || ''}</span> : null}
          </div>
        </td>
        <td className="num">{fmt(pl)}</td>
        <td className="num">{fmt(ac)}</td>
        <td className={`num ${varClass(tv)}`}>{varStr(tv)}</td>
      </tr>,
    ];

    phases.forEach((r) => {
      const paid = paymentsFor(oppId, r.phase).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const v = actualOf(r, oppId) - (Number(r.planned) || 0);
      const typed = r.manual != null && (r.manual as unknown) !== '';
      rows.push(
        <tr className="phrow" key={`${id}-${r.phase}`} style={{ display: open ? '' : 'none' }}>
          <td className="ind" />
          <td className="ph-cell">
            <span
              className="pm-bt-phbox"
              style={{ background: PHASE_COLORS[r.phase] || '#b0bec5' }}
              title={r.label}
            />
            <span className="pm-bt-phlabel">{r.label}</span>
          </td>
          <td className="num">{fmt(r.planned)}</td>
          <td className="num">
            <input
              type="number"
              className="pm-bt-ovinput"
              min={0}
              step={1000}
              /* The ledger total shows through as the placeholder — that is the fallback. */
              placeholder={paid > 0 ? String(Math.round(paid)) : '0'}
              value={typed ? String(r.manual) : ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setManual(id, r.phase, e.target.value)}
            />
          </td>
          <td className={`num ${varClass(v)}`}>{varStr(v)}</td>
        </tr>,
      );
    });
    return rows;
  };

  const payments = prog
    ? [...prog.payments].sort(
        (a, b) =>
          String(a.payment_date || '').localeCompare(String(b.payment_date || '')) || a.id - b.id,
      )
    : [];
  const ledgerSum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  return (
    <div
      className="page"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <HelpPanel helps={BT_HELPS.map((h) => withFinalizedRule(h))} />
      <div className="pm-bt-head">
        {/* Carries the selected programme back with it, as the source's link does. */}
        <button
          className="pm-bt-back"
          onClick={() =>
            navigate(
              currentProject
                ? `/project_management/program-management/delivery-execution/execution-control-center?project=${encodeURIComponent(currentProject)}`
                : '/project_management/program-management/delivery-execution/execution-control-center',
            )
          }
        >
          <i className="material-icons" style={{ fontSize: 16 }}>
            arrow_back
          </i>{' '}
          Execution Control Center
        </button>
        <div className="pm-bt-titleblock">
          <i className="material-icons" style={{ fontSize: '2.1rem', color: '#00897b' }}>
            payments
          </i>
          <div>
            <h1 className="page-header__title">Budget Tracker</h1>
            <p>
              Actual spend vs. plan, per opportunity and phase — with a dedicated payments ledger
              tab.
            </p>
          </div>
        </div>
        <div className="pm-bt-toolbar">
          <div className="pm-bt-fg">
            <SelectField
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={currentProject}
              onChange={(v) => void onSelect(v)}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          </div>
          {prog ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              {activeTab === 'breakdown' ? (
                <>
                  <button className="pm-bt-linkbtn" onClick={() => expandAll(true)}>
                    Expand all
                  </button>
                  <button className="pm-bt-linkbtn" onClick={() => expandAll(false)}>
                    Collapse all
                  </button>
                  <span style={{ color: '#cfd8dc' }}>|</span>
                </>
              ) : null}
              <span className="pm-bt-summary">
                {fmt(totals.actual)} / {fmt(totals.planned)}
              </span>
              <button className="pm-bt-save" disabled={!dirty || saving} onClick={() => void save()}>
                {saveLabel}
              </button>
            </div>
          ) : null}
        </div>
        <div className="pm-bt-tabs">
          <button
            className={`pm-bt-tab${activeTab === 'breakdown' ? ' active' : ''}`}
            onClick={() => setActiveTab('breakdown')}
          >
            <i className="material-icons" style={{ fontSize: 17 }}>
              account_tree
            </i>{' '}
            Budget breakdown
          </button>
          <button
            className={`pm-bt-tab${activeTab === 'ledger' ? ' active' : ''}`}
            onClick={() => setActiveTab('ledger')}
          >
            <i className="material-icons" style={{ fontSize: 17 }}>
              receipt_long
            </i>{' '}
            Payments ledger <span className="tcount">{payments.length}</span>
          </button>
        </div>
      </div>

      <div className="pm-bt-body">
        {error ? (
          <div className="pm-bt-empty" style={{ color: '#c62828' }}>
            {error}
          </div>
        ) : null}

        {activeTab === 'breakdown' ? (
          !prog ? (
            <div className="pm-bt-empty">
              Select a program to track actual spend by opportunity and phase.
            </div>
          ) : (
            <>
              <div className="pm-bt-statrow">
                <div className="pm-bt-stat">
                  <i className="material-icons ic" style={{ color: '#607d8b' }}>
                    event_note
                  </i>
                  <div>
                    <div className="lbl">Planned</div>
                    <div className="val">{fmt(totals.planned)}</div>
                    <div className="sub">Shared + all opportunities</div>
                  </div>
                </div>
                <div className="pm-bt-stat">
                  <i className="material-icons ic" style={{ color: '#00897b' }}>
                    payments
                  </i>
                  <div>
                    <div className="lbl">Actual</div>
                    <div className="val" style={{ color: '#00695c' }}>
                      {fmt(totals.actual)}
                    </div>
                    <div className="sub">manual + logged payments</div>
                  </div>
                </div>
                <div className="pm-bt-stat">
                  <i
                    className="material-icons ic"
                    style={{ color: totalVar > 0 ? '#c62828' : '#2e7d32' }}
                  >
                    {totalVar > 0 ? 'trending_up' : 'trending_down'}
                  </i>
                  <div>
                    <div className="lbl">Variance</div>
                    <div className={`val ${varClass(totalVar)}`}>
                      {totalVar > 0.5 ? '+' : ''}
                      {fmt(totalVar)}
                    </div>
                    <div className="sub">
                      {totalVar > 0.5 ? 'over plan' : totalVar < -0.5 ? 'under plan' : 'on plan'}
                    </div>
                  </div>
                </div>
              </div>

              <table className="pm-bt-grid">
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 170 }} />
                  <col style={{ width: 150 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="ind" />
                    <th>Scope / Phase</th>
                    <th className="num">Planned</th>
                    <th className="num">Actual</th>
                    <th className="num">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {renderScope('shared', '🌐 Shared / Project', null)}
                  {prog.opportunities.map((op) =>
                    renderScope(`opp:${op.opportunity_id}`, `📌 ${op.opportunity_id}`, op),
                  )}
                </tbody>
              </table>
            </>
          )
        ) : (
          <>
            <div className="pm-bt-ledgerbar">
              <span className="lb-title">
                <i className="material-icons" style={{ color: '#00897b' }}>
                  receipt_long
                </i>{' '}
                Payments ledger <span className="lb-sum">{fmt(ledgerSum)}</span>
              </span>
            </div>
            {!prog ? (
              <div className="pm-bt-plempty">Select a program to see and log payments.</div>
            ) : (
              <>
                <div className="pm-bt-addbar">
                  <div className="fld">
                    <label>Scope</label>
                    <select
                      className="pm-bt-in scope"
                      value={payScope}
                      onChange={(e) => setPayScope(e.target.value)}
                    >
                      <option value="">🌐 Shared / Project</option>
                      {prog.opportunities.map((o) => (
                        <option key={o.opportunity_id} value={o.opportunity_id}>
                          📌 {o.opportunity_id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Phase</label>
                    <select
                      className="pm-bt-in phase"
                      value={payPhase}
                      onChange={(e) => setPayPhase(e.target.value)}
                    >
                      {prog.phases.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Vendor</label>
                    <input
                      type="text"
                      className="pm-bt-in vendor"
                      placeholder="Supplier"
                      value={payVendor}
                      onChange={(e) => setPayVendor(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>Invoice #</label>
                    <input
                      type="text"
                      className="pm-bt-in inv"
                      placeholder="optional"
                      value={payInvoice}
                      onChange={(e) => setPayInvoice(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>Date</label>
                    <input
                      type="date"
                      className="pm-bt-in dt"
                      value={payDate}
                      onChange={(e) => setPayDate(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>Amount ($)</label>
                    <input
                      type="number"
                      min={0}
                      step={1000}
                      className="pm-bt-in num"
                      placeholder="0"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <button className="pm-bt-addbtn" onClick={() => void addPayment()}>
                      + Add
                    </button>
                  </div>
                </div>

                <div className="pm-bt-plwrap">
                  {!payments.length ? (
                    <div className="pm-bt-plempty">
                      No payments logged yet. Add one above — it rolls up into that phase&apos;s
                      Actual on the breakdown tab.
                    </div>
                  ) : (
                    <table className="pm-bt-pltable">
                      <thead>
                        <tr>
                          <th>Scope</th>
                          <th style={{ width: 150 }}>Phase</th>
                          <th style={{ width: 210 }}>Vendor</th>
                          <th style={{ width: 110 }}>Invoice #</th>
                          <th style={{ width: 120 }}>Date</th>
                          <th className="num" style={{ width: 120 }}>
                            Amount
                          </th>
                          <th style={{ width: 34 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => {
                          const oppId = p.opportunity_id || '';
                          return (
                            <tr key={p.id}>
                              <td className={`pm-bt-plscope ${oppId ? 'opp' : 'shared'}`}>
                                {scopeName(oppId)}
                              </td>
                              <td>{phaseLabel(p.phase)}</td>
                              <td>{p.vendor || '—'}</td>
                              <td>{p.invoice_number || '—'}</td>
                              <td>{p.payment_date || '—'}</td>
                              <td className="num">{fmt(p.amount)}</td>
                              <td style={{ textAlign: 'center', width: 36 }}>
                                <button
                                  className="pm-bt-pldel"
                                  title="Delete"
                                  onClick={() => void delPayment(p.id)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
